import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, FormGroup, Row } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import Select from "react-select";
import axios from "axios";

const ProductReports = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;
  const [productHistory, setProductHistory] = useState({});
  const [LineColumnAreaData, setLineColumnAreaData] = useState({
    series: [],
    options: {},
  });
  const [regionOptions, setRegionOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState("");

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const regionResponse = await axios.get(`${process.env.REACT_APP_API_BASEURL}/region`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = regionResponse.data;

      const productHistoryData = data.reduce((acc, region) => {
        acc[region.regionName] = region.productHistory;
        return acc;
      }, {});

      setProductHistory(productHistoryData);
      const versions = new Set();
      const productNames = new Set();

      Object.values(productHistoryData).forEach((history) => {
        history.forEach((entry) => {
          versions.add(entry.version);
          entry.product.forEach((product) => {
            productNames.add(product.productName);
          });
        });
      });
      const transformedRegionOptions = regionResponse?.data?.map((region) => ({
        value: region._id,
        label: region.regionName,
      }));
      setRegionOptions(transformedRegionOptions);

      if (transformedRegionOptions.length > 0) {
        setSelectedRegion(transformedRegionOptions[0].label);
      }

      const quarterCount = new Map();
      const quarterNames = new Set();

      Object.entries(productHistoryData).forEach(([regionName, data]) => {
        const regionQuarterCount = new Map();
        data.forEach((history) => {
          const quarter = history.quarter;
          const currentCount = regionQuarterCount.get(quarter) || 0;
          regionQuarterCount.set(quarter, currentCount + 1);
          quarterNames.add(quarter);
        });
        quarterCount.set(regionName, regionQuarterCount);
      });

      const parseQuarter = (quarter) => {
        const match = quarter.match(/Q(\d) (\d{4})/);
        if (match) {
          const [, quarterNumber, year] = match;
          return {
            quarterNumber: parseInt(quarterNumber, 10),
            year: parseInt(year, 10),
            original: quarter,
          };
        }
        return { quarterNumber: 0, year: 0, original: quarter };
      };

      const customSort = (a, b) => {
        const parsedA = parseQuarter(a);
        const parsedB = parseQuarter(b);
        const customOrder = { 2: 0, 3: 1, 4: 2, 1: 3 };
        if (parsedA.year === parsedB.year) {
          return (
            customOrder[parsedA.quarterNumber] -
            customOrder[parsedB.quarterNumber]
          );
        }
        return parsedA.year - parsedB.year;
      };

      const sortedQuarters = Array.from(quarterNames).sort(customSort);
      setSelectedQuarter(sortedQuarters[0]);
      const transformedQuarterOptions = sortedQuarters.map((quarter) => ({
        value: quarter,
        label: quarter,
      }));
      setQuarterOptions(transformedQuarterOptions);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRegionChange1 = (selectedRegionName) => {
    const selectedRegionName1 = selectedRegionName.label;
    setSelectedRegion(selectedRegionName1);
  };

  const handleQuarterChange = (selectedQuarterOption) => {
    setSelectedQuarter(selectedQuarterOption.value);
  };

  useEffect(() => {
    if (selectedRegion && selectedQuarter) {
      const regionHistory = productHistory[selectedRegion];

      if (regionHistory) {
        const filteredHistory = regionHistory.filter(
          (entry) => entry.quarter === selectedQuarter
        );
        const products = [
          ...new Set(
            filteredHistory.flatMap((entry) =>
              entry.product
                .filter((product) => product.productName)
                .map((product) => product.productName)
            )
          ),
        ];
        const versionData = {};
        products.forEach((productName) => {
          versionData[productName] = {};
          filteredHistory.forEach((entry) => {
            const version = entry.version;
            if (!versionData[productName][version]) {
              versionData[productName][version] = 0;
            }
            const product = entry.product.find(
              (product) => product.productName === productName
            );
            if (product) {
              versionData[productName][version] += product.quantity;
            }
          });
        });
        const chartNumbers = Array.from(Array(filteredHistory.length).keys());
        const chartSeries = products.map((productName) => ({
          name: productName,
          data: chartNumbers.map(
            (_, index) =>
              versionData[productName][filteredHistory[index].version] || 0
          ),
        }));
        setLineColumnAreaData({
          series: chartSeries,
          options: {
            chart: {
              height: 500,
              stacked: false,
              toolbar: {
                show: true,
                offsetX: 0,
                offsetY: 0,
                tools: {
                  download:
                    "<button type='button' style=background-color:#e42626;min-width:130px;width:max-content;border-radius:8px;padding:8px;border:none;color:white;position:fixed;top:-50px;bottom:25px;left:-115px;min-height:40px;><svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='white' class='bi bi-file-earmark-arrow-up' viewBox='0 0 16 16'><path d='M8.5 11.5a.5.5 0 0 1-1 0V7.707L6.354 8.854a.5.5 0 1 1-.708-.708l2-2a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 7.707z'/><path d='M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z'/></svg> Export data</button>",
                  selection: false,
                  zoom: false,
                  zoomin: false,
                  zoomout: false,
                  pan: false,
                  reset: false,
                  customIcons: [],
                },
                export: {
                  csv: {
                    filename: selectedRegion + "-" + selectedQuarter + "-CSV",
                    columnDelimiter: ",",
                    headerCategory: "Category",
                    headerValue: "Value",
                    categoryFormatter(x) {
                      return new Date(x).toDateString();
                    },
                    valueFormatter(y) {
                      return y;
                    },
                  },
                  svg: {
                    filename: selectedRegion + "-" + selectedQuarter + "-SVG",
                  },
                  png: {
                    filename: selectedRegion + "-" + selectedQuarter + "-PNG",
                  },
                },
                autoSelected: "zoom",
              },
            },
            stroke: {
              width: [1, 1, 1],
              dashArray: [3, 0, 5],
              curve: "smooth",
            },
            legend: {
              show: true,
            },
            colors: [
              "#e42626",
              "#4E4E4e",
              "#000000",
              "#e42626",
              "#4E4E4e",
              "#000000",
            ],
            xaxis: {
              categories: chartNumbers,
              title: {
                text: "Versions",
              },
            },
            yaxis: {
              title: {
                text: "Quantities",
              },
            },
            tooltip: {
              shared: true,
              intersect: false,
              y: {
                formatter: function (y) {
                  if (typeof y !== "undefined") {
                    return y.toFixed(0);
                  }
                  return y;
                },
              },
              style: {
                fontSize: "12px",
                color: "red",
              },
            },
            grid: {
              borderColor: "#f1f1f1",
            },
            dataLabels: {
              enabled: false,
            },
          },
        });
      }
    }
  }, [selectedQuarter, productHistory, selectedRegion]);

  return (
    <React.Fragment>
      <Row>
        <Col xl={14}>
          <Card>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginRight: "135px",
                  position: "relative",
                  top: "15px",
                }}
              >
                <div>
                  <h5 className="card-title">Product Report</h5>
                </div>
                <div>
                  <div className="d-flex gap-4">
                    <div className="flex-shrink-0">
                      <FormGroup>
                        <Select
                          showSearch
                          name="region"
                          options={regionOptions}
                          classNamePrefix="select"
                          onChange={(selectedOptions) =>
                            handleRegionChange1(selectedOptions)
                          }
                          value={
                            selectedRegion
                              ? regionOptions.find(
                                (option) => option.label === selectedRegion
                              )
                              : regionOptions[0]
                          }
                          defaultValue={regionOptions[0]}
                          styles={{
                            menu: (provided) => ({
                              ...provided,
                              maxHeight: "165px",
                              overflowY: "auto",
                            }),
                            menuList: (provided) => ({
                              ...provided,
                              maxHeight: "165px",
                              overflowY: "auto",
                            }),
                          }}
                        />
                      </FormGroup>
                    </div>
                    <div className="flex-shrink-0">
                      <FormGroup>
                        <Select
                          showSearch
                          options={quarterOptions}
                          value={
                            selectedQuarter
                              ? quarterOptions.find(
                                (option) => option.label === selectedQuarter
                              )
                              : null
                          }
                          onChange={handleQuarterChange}
                          placeholder="Select Quarter"
                          defaultValue={
                            quarterOptions.length > 0
                              ? quarterOptions.find(
                                (option) => option.value === selectedQuarter
                              )
                              : null
                          }
                          styles={{
                            menu: (provided) => ({
                              ...provided,
                              maxHeight: "165px",
                              overflowY: "auto",
                            }),
                            menuList: (provided) => ({
                              ...provided,
                              maxHeight: "165px",
                              overflowY: "auto",
                            }),
                          }}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </div>
              </div>
              <Row>
                <Col>
                  {Object.keys(productHistory).length > 0 ? (
                    <div style={{ alignContent: "center" }}>
                      {LineColumnAreaData.series.length > 0 ? (
                        <ReactApexChart
                          options={LineColumnAreaData.options}
                          series={LineColumnAreaData.series}
                          type="bar"
                          stacked="false"
                          className="apex-charts"
                        />
                      ) : (
                        <div style={{ height: "130px" }}>
                          There are no changes of <b>{selectedRegion}</b> in{" "}
                          <b>{selectedQuarter}</b>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>No records found.</div>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default ProductReports;
